/**
 * Search Wikimedia Tool
 *
 * Searches Wikimedia Commons for images.
 * Returns search results without downloading them.
 *
 * Based on WikimediaSearcher from big-slide-gen.
 */
import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { WIKIMEDIA_API_URL, WIKIMEDIA_USER_AGENT } from "../../config";
import { SearchWikimediaParamsSchema, } from "./searchWikimedia.schema";
// ═══════════════════════════════════════════════════════════════════════════
// RELEVANCE SCORING
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Calculate relevance score for search result.
 */
function calculateRelevance(query, fileName, description) {
    const queryLower = query.toLowerCase();
    const fileLower = fileName.toLowerCase();
    // Split query into terms
    const queryTerms = queryLower.split(/\s+/).filter(t => t.length > 2);
    if (queryTerms.length === 0)
        return 0.5;
    // Count matches in filename
    const fileMatches = queryTerms.filter(term => fileLower.includes(term)).length;
    let score = fileMatches / queryTerms.length;
    // Boost if description contains query terms
    if (description) {
        const descLower = description.toLowerCase();
        const descMatches = queryTerms.filter(term => descLower.includes(term)).length;
        score += (descMatches / queryTerms.length) * 0.3;
    }
    return Math.min(score, 1.0);
}
/**
 * Check if image matches the requested type.
 */
function matchesImageType(imageType, width, height) {
    if (imageType === 'general') {
        return true;
    }
    if (imageType === 'executive_headshot') {
        // Check if portrait orientation (height > width)
        if (width > 0 && height > 0) {
            const aspectRatio = height / width;
            return aspectRatio >= 1.2; // Must be reasonably portrait
        }
        return false;
    }
    if (imageType === 'product_image') {
        // Product images can be any orientation, just ensure reasonable size
        return width >= 200 && height >= 200;
    }
    return true;
}
/**
 * Strip HTML tags from description.
 */
function stripHtml(html) {
    return html.replace(/<[^>]*>/g, '').trim();
}
// ═══════════════════════════════════════════════════════════════════════════
// WIKIMEDIA API CLIENT
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Search Wikimedia Commons for images.
 */
async function searchWikimediaApi(query, limit, imageType) {
    try {
        // Build search params - use generator search to get image info
        const params = new URLSearchParams({
            action: 'query',
            generator: 'search',
            gsrsearch: `filetype:bitmap ${query}`, // Search for bitmap images
            gsrnamespace: '6', // File namespace
            gsrlimit: String(limit * 3), // Get extra to filter by type
            prop: 'imageinfo',
            iiprop: 'url|size|extmetadata',
            iiurlwidth: '300', // Request thumbnail
            format: 'json',
            origin: '*', // CORS
        });
        const response = await fetch(`${WIKIMEDIA_API_URL}?${params}`, {
            method: 'GET',
            headers: {
                'Api-User-Agent': WIKIMEDIA_USER_AGENT,
            },
        });
        if (!response.ok) {
            console.warn(`[SearchWikimedia] API returned ${response.status}`);
            return [];
        }
        const data = await response.json();
        const pages = data.query?.pages || {};
        // Process results
        const results = [];
        for (const pageData of Object.values(pages)) {
            const imageinfo = pageData.imageinfo?.[0];
            if (!imageinfo)
                continue;
            const width = imageinfo.width || 0;
            const height = imageinfo.height || 0;
            // Filter by image type
            if (!matchesImageType(imageType, width, height)) {
                continue;
            }
            // Extract metadata
            const extmetadata = imageinfo.extmetadata || {};
            const rawDescription = extmetadata.ImageDescription?.value || '';
            const description = stripHtml(rawDescription);
            const license = extmetadata.LicenseShortName?.value || 'Unknown';
            // Clean up file name
            const fileName = pageData.title.replace(/^File:/, '');
            // Calculate relevance
            const relevance = calculateRelevance(query, fileName, description);
            results.push({
                fileName,
                title: fileName.replace(/_/g, ' ').replace(/\.\w+$/, ''),
                description: description.slice(0, 500), // Truncate long descriptions
                url: imageinfo.url,
                dimensions: { width, height },
                license,
                thumbUrl: imageinfo.thumburl,
                _relevance: relevance, // Internal, for sorting
            });
        }
        // Sort by relevance and limit
        results.sort((a, b) => (b._relevance || 0) - (a._relevance || 0));
        // Remove internal relevance field and return limited results
        return results.slice(0, limit).map(r => {
            const { _relevance, ...rest } = r;
            return rest;
        });
    }
    catch (error) {
        console.error('[SearchWikimedia] Search error:', error);
        return [];
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// SEARCH WIKIMEDIA TOOL
// ═══════════════════════════════════════════════════════════════════════════
// System prompt fallback for Keywords AI
const SEARCH_WIKIMEDIA_SYSTEM_PROMPT = `### searchWikimedia
Search Wikimedia Commons for images.

**When to use:**
- Finding stock images, photos, diagrams
- Getting headshots of public figures
- Finding maps, flags, logos not in Brandfetch

**Image types:**
- "photo" - Photographs
- "diagram" - Charts, diagrams, infographics
- "map" - Geographic maps
- "logo" - Company/organization logos
- "any" - All types

**This tool only searches** - call retrieveWikimedia to download.

\`\`\`json
{
  "query": "Satya Nadella",
  "imageType": "photo",
  "maxResults": 10
}
\`\`\`

Returns: search results with pageIds, dimensions, licenses.`;
/**
 * Tool for searching Wikimedia Commons for images.
 *
 * Does NOT download images - use retrieveWikimedia for that.
 */
export class SearchWikimediaTool extends ToolBase {
    constructor() {
        super();
        this.name = "searchWikimedia";
        // System prompt description
        this.systemPromptDescription = SEARCH_WIKIMEDIA_SYSTEM_PROMPT;
        // Streaming operation fields
        this.fieldsToCheck = [
            { name: 'uiDescription', type: 'string', list_status: false, order: 0 },
            { name: 'query', type: 'string', list_status: false, order: 1 },
            { name: 'imageType', type: 'string', list_status: false, order: 2 },
            { name: 'limit', type: 'number', list_status: false, order: 3 },
        ];
        this.listsToCheck = [];
        this.description = new ToolDescription("searchWikimedia", `Search Wikimedia Commons for images.

**Purpose:** Find images from Wikimedia Commons (executive headshots, product images, etc.)

**Parameters:**
- query: Search term (e.g., "Satya Nadella", "iPhone 15")
- imageType: "executive_headshot", "product_image", or "general"
- limit: Maximum results (default: 10)

**Example:**
\`\`\`json
{
  "query": "Satya Nadella",
  "imageType": "executive_headshot",
  "limit": 10
}
\`\`\`

**Returns:**
- results: Array of matching images with:
  - fileName: Wikimedia file name
  - description: Original Wikimedia description
  - dimensions: Image size
  - license: License type

**Note:** Use retrieveWikimedia to download specific images after searching.`, SearchWikimediaParamsSchema);
    }
    async execute(parameters) {
        const parseResult = safeParseToolParams(SearchWikimediaParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }
        const params = parseResult.data;
        const limit = params.limit ?? 10;
        console.log(`[SearchWikimediaTool] Searching for "${params.query}" (type: ${params.imageType}, limit: ${limit})`);
        try {
            const results = await searchWikimediaApi(params.query, limit, params.imageType);
            const response = {
                success: true,
                query: params.query,
                results,
                totalFound: results.length,
            };
            console.log(`[SearchWikimediaTool] ✅ Found ${results.length} results`);
            return response;
        }
        catch (error) {
            console.error("[SearchWikimediaTool] ❌ Error:", error);
            return {
                success: false,
                query: params.query,
                results: [],
                totalFound: 0,
                error: `Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            };
        }
    }
}
